import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { QcStamp } from '../../components/QcStamp';
import type {
  KbReadiness,
  KbReadinessItem,
  KbReadinessKey,
  KbReadinessLevel,
  KbReadinessStatus,
} from '../../types/api';
import { knowledgeBaseSectionPath, type KnowledgeBaseSection } from '../knowledge-base/sections';
import { pickReadinessGap, READINESS_ORDER, READINESS_SECTIONS } from './readinessRows';
import { useKbReadiness } from '../knowledge-base/useKbReadiness';
import { useBrandCopy } from '../onboarding/useBrandCopy';
import type { BrandKindCopy } from '../onboarding/brandKind';

interface ItemSpec {
  label: string;
  /** Where in the knowledge base this gap gets filled. */
  section: KnowledgeBaseSection;
  /** One line on what the drafts lose without it. */
  nudge: string;
}

function itemSpec(copy: BrandKindCopy, key: KbReadinessKey): ItemSpec {
  return { ...copy.readiness[key], section: READINESS_SECTIONS[key] };
}

const STATUS_MARKS: Record<KbReadinessStatus, { glyph: string; word: string }> = {
  complete: { glyph: '✓', word: 'Complete' },
  partial: { glyph: '◐', word: 'Partial' },
  missing: { glyph: '○', word: 'Missing' },
};

const LEVEL_LABELS: Record<KbReadinessLevel, string> = {
  thin: 'Thin',
  basic: 'Basic',
  strong: 'Strong',
};

/** Only `strong` has cleared the bar; `thin` and `basic` are open QC items. */
function isCleared(level: KbReadinessLevel): boolean {
  return level === 'strong';
}

interface ReadinessRow {
  key: KbReadinessKey;
  item: KbReadinessItem;
  spec: ItemSpec;
}

function toRows(readiness: KbReadiness, copy: BrandKindCopy): ReadinessRow[] {
  return READINESS_ORDER.flatMap((key) => {
    const item = readiness.items.find((entry) => entry.key === key);
    return item ? [{ key, item, spec: itemSpec(copy, key) }] : [];
  });
}

function ReadinessDetail({
  readiness,
  isFolded,
  onToggle,
}: {
  readiness: KbReadiness;
  isFolded: boolean;
  onToggle: () => void;
}) {
  const listId = useId();
  const copy = useBrandCopy();
  const rows = toRows(readiness, copy);
  const complete = rows.filter((row) => row.item.status === 'complete').length;
  const cleared = isCleared(readiness.level);
  const gapItem = pickReadinessGap(rows.map((row) => row.item));
  const gap = rows.find((row) => row.item === gapItem) ?? null;

  return (
    <>
      <div className="readiness__head">
        <div>
          <p
            className={cleared ? 'readiness__score readiness__score--cleared' : 'readiness__score'}
          >
            {readiness.score}
          </p>
          <p className="readiness__score-label">How well we know you, out of 100</p>
        </div>

        {cleared ? (
          <QcStamp
            label={LEVEL_LABELS[readiness.level]}
            sub={`${complete} of ${rows.length} filled in`}
          />
        ) : (
          <span className="readiness__open">
            <span className="readiness__open-label">{LEVEL_LABELS[readiness.level]}</span>
            <span className="readiness__open-sub">
              {complete} of {rows.length} filled in
            </span>
          </span>
        )}
      </div>

      {gap ? (
        <p className="readiness__nudge">
          {gap.spec.nudge}{' '}
          <Link to={knowledgeBaseSectionPath(gap.spec.section)}>
            Open {gap.spec.label.toLowerCase()}
          </Link>
        </p>
      ) : (
        <p className="readiness__nudge">
          Everything is filled in. Posts are written from your details, not guesses.
        </p>
      )}

      {/* Narrow screens fold the eleven rows away so the tabs stay near the top;
        CSS hides this button (and shows the rows) wherever there is room. */}
      <button
        className="button button--ghost readiness__toggle"
        type="button"
        aria-expanded={!isFolded}
        aria-controls={listId}
        onClick={onToggle}
      >
        {isFolded ? `Show all ${rows.length} checks` : 'Hide the checks'}
      </button>

      <ul className="readiness__list" id={listId}>
        {rows.map((row) => {
          const mark = STATUS_MARKS[row.item.status];
          return (
            <li className="readiness__row" key={row.key}>
              <Link className="readiness__link" to={knowledgeBaseSectionPath(row.spec.section)}>
                {row.spec.label}
              </Link>
              <span className="readiness__count">
                {row.item.count}/{row.item.target}
              </span>
              <span className="visually-hidden">{mark.word}</span>
              <span
                className={`readiness__mark readiness__mark--${row.item.status}`}
                aria-hidden="true"
              >
                {mark.glyph}
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/**
 * Brand-profile readiness as a traveler card: the score ("How well it knows
 * you"), the eleven checks that make it up, and one line on the gap that
 * matters most (`pickReadinessGap`).
 *
 * Grounding quality is what the whole pipeline rests on (SRS §3 stage 1), so
 * this is the knowledge base's standing readout, at the top of that page; the
 * home screen carries a one-line summary of it (`ReadinessLine`).
 */
export function KbReadinessCard({ businessId }: { businessId: string }) {
  const { readiness, isLoading, error } = useKbReadiness(businessId);
  const [isFolded, setIsFolded] = useState(true);

  return (
    <section
      className={isFolded ? 'readiness readiness--folded' : 'readiness'}
      aria-label="Profile strength"
    >
      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : isLoading || readiness === null ? (
        <p className="kb-section__status" role="status">
          Reading your brand profile
        </p>
      ) : (
        <ReadinessDetail
          readiness={readiness}
          isFolded={isFolded}
          onToggle={() => setIsFolded((value) => !value)}
        />
      )}
    </section>
  );
}
